import { ReactNode, useEffect, useId, useRef, useState } from 'react';

/**
 * A button that opens a panel of actions beneath it.
 *
 * Disclosure semantics (a button with aria-expanded controlling a region), not
 * an ARIA menu: role="menu" promises arrow-key roving focus and type-ahead,
 * and a widget that claims those without implementing them is worse than a
 * plain list of buttons that Tab moves through as expected.
 *
 * Escape closes and returns focus to the trigger, so keyboard users are not
 * dropped at the top of the document. A click outside closes. Choosing
 * anything inside closes, so the panel never sits over the screen someone
 * just navigated to.
 */
export function Dropdown({ trigger, label, className = '', disabled = false, children }: {
  trigger: ReactNode;
  /** Accessible name for the trigger — it is usually an icon or an avatar. */
  label: string;
  className?: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); }
    }
    function onPointer(e: PointerEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  // A test starting while the panel is open must not leave it floating there.
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  function onPanelClick(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('button, a')) setOpen(false);
  }

  return (
    <div className="dropdown" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`dropdown__trigger ${className}`}
        aria-label={label}
        data-tip={label}
        aria-expanded={open}
        aria-controls={panelId}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger}
      </button>
      <div id={panelId} className={`dropdown__panel${open ? ' is-open' : ''}`} onClick={onPanelClick}>
        {children}
      </div>
    </div>
  );
}
